import React, { useState } from "react"
import { UserInterface } from "../../App"
import styles from "./Messaging.module.scss"

type Msg = { text: string; sender: string; ts: number }

const Messaging: React.FC<{ user: UserInterface }> = ({ user }) => {
    const channels = ["#ai", "#general", "#dev", "#tasks"]
    const directs = [user?.name]

    const [active, setActive] = useState<string>("#general")
    const [input, setInput] = useState<string>("")
    const [chats, setChats] = useState<Record<string, Msg[]>>({})

    const activeMessages = chats[active] || []

    const setActiveAndEnsureThread = (key: string) => {
        setActive(key)
        if (!chats[key]) {
            setChats({ ...chats, [key]: [] })
        }
    }

    const sendMessage = () => {
        const text = input.trim()
        if (!text) return
        const msg: Msg = { text, sender: user.name, ts: Date.now() }
        setChats((prev) => ({ ...prev, [active]: [...(prev[active] || []), msg] }))
        setInput("")
    }

    const initial = (name: string) => (name?.trim()?.charAt(0) || "?").toUpperCase()

    const formatDay = (ms: number) => {
        const d = new Date(ms)
        const today = new Date()
        const one = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
        const diff = (one(today) - one(d)) / 86400000
        if (diff === 0) return "Today"
        if (diff === 1) return "Yesterday"
        return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })
    }

    const formatTime = (ms: number) =>
        new Date(ms).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })

    let lastDay = ""

    return (
        <div className={styles.Messaging}>
            <div className={styles.sidebar}>
                <div className={styles.sectionTitle}>CHANNELS</div>
                <ul className={styles.list}>
                    {channels.map((ch) => (
                        <li
                            key={ch}
                            className={`${styles.item} ${active === ch ? styles.active : ""}`}
                            onClick={() => setActiveAndEnsureThread(ch)}>
                            {ch}
                        </li>
                    ))}
                </ul>

                <br />
                <div className={styles.sectionTitle}>DIRECT</div>
                <ul className={styles.list}>
                    {directs.map((dm) => (
                        <li
                            key={dm}
                            className={`${styles.item} ${active === dm ? styles.active : ""}`}
                            onClick={() => setActiveAndEnsureThread(dm)}>
                            {dm}
                        </li>
                    ))}
                </ul>
            </div>

            <div className={styles.container}>
                <div className={styles.nav}>{active}</div>

                <div className={styles.content}>
                    {activeMessages.length === 0 && (
                        <p className={styles.empty}>No messages yet. Say hi.</p>
                    )}

                    {activeMessages.map((m, idx) => {
                        const day = formatDay(m.ts)
                        const showDivider = day !== lastDay
                        lastDay = day
                        return (
                            <React.Fragment key={idx}>
                                {showDivider && (
                                    <div className={styles.dateDivider}>
                                        <span>{day}</span>
                                    </div>
                                )}
                                <div
                                    className={`${styles.messageRow} ${
                                        m.sender === user.name ? styles.me : ""
                                    }`}>
                                    <div className={styles.avatar}>{initial(m.sender)}</div>
                                    <div className={styles.bubble}>
                                        <div className={styles.meta}>
                                            <span className={styles.sender}>{m.sender}</span>
                                            <span className={styles.time}>{formatTime(m.ts)}</span>
                                        </div>
                                        <div className={styles.text}>{m.text}</div>
                                    </div>
                                </div>
                            </React.Fragment>
                        )
                    })}
                </div>

                <div className={styles.input}>
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={`Message ${active}`}
                        onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                    />
                    <button onClick={sendMessage}>
                        <i className="material-symbols-rounded">arrow_forward</i>
                    </button>
                </div>
            </div>
        </div>
    )
}

export default Messaging
